import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/app/AppLayout";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";

const AuthPage = lazy(() => import("@/features/auth/AuthPage").then((m) => ({ default: m.AuthPage })));
const LandingPage = lazy(() => import("@/features/landing/LandingPage").then((m) => ({ default: m.LandingPage })));
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const TeamActivationPage = lazy(() => import("@/features/auth/TeamActivationPage").then((m) => ({ default: m.TeamActivationPage })));
const AdminLoginPage = lazy(() => import("@/features/admin/AdminLoginPage").then((m) => ({ default: m.AdminLoginPage })));
const AdminDashboardPage = lazy(() => import("@/features/admin/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage })));
const AdminPlansPage = lazy(() => import("@/features/admin/AdminPlansPage").then((m) => ({ default: m.AdminPlansPage })));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const ClientsPage = lazy(() => import("@/features/clients/ClientsPage").then((m) => ({ default: m.ClientsPage })));
const ClientCreatePage = lazy(() => import("@/features/clients/ClientCreatePage").then((m) => ({ default: m.ClientCreatePage })));
const ClientEditPage = lazy(() => import("@/features/clients/ClientEditPage").then((m) => ({ default: m.ClientEditPage })));
const ClientLayout = lazy(() => import("@/features/clients/ClientLayout").then((m) => ({ default: m.ClientLayout })));
const ClientOverviewTab = lazy(() => import("@/features/clients/ClientOverviewTab").then((m) => ({ default: m.ClientOverviewTab })));
const ClientOrdersTab = lazy(() => import("@/features/clients/ClientOrdersTab").then((m) => ({ default: m.ClientOrdersTab })));
const ClientMeasurementsTab = lazy(() => import("@/features/clients/ClientMeasurementsTab").then((m) => ({ default: m.ClientMeasurementsTab })));
const ClientMeasurementsEditPage = lazy(() => import("@/features/clients/ClientMeasurementsEditPage").then((m) => ({ default: m.ClientMeasurementsEditPage })));
const ClientPaymentsTab = lazy(() => import("@/features/clients/ClientPaymentsTab").then((m) => ({ default: m.ClientPaymentsTab })));
const ClientStylesTab = lazy(() => import("@/features/clients/ClientStylesTab").then((m) => ({ default: m.ClientStylesTab })));
const MeasurementsPage = lazy(() => import("@/features/measurements/MeasurementsPage").then((m) => ({ default: m.MeasurementsPage })));
const OrdersPage = lazy(() => import("@/features/orders/OrdersPage").then((m) => ({ default: m.OrdersPage })));
const OrderDetailPage = lazy(() => import("@/features/orders/OrderDetailPage").then((m) => ({ default: m.OrderDetailPage })));
const PaymentsPage = lazy(() => import("@/features/payments/PaymentsPage").then((m) => ({ default: m.PaymentsPage })));
const FinancePage = lazy(() => import("@/features/finance/FinancePage").then((m) => ({ default: m.FinancePage })));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const StylesPage = lazy(() => import("@/features/styles/StylesPage").then((m) => ({ default: m.StylesPage })));
const StyleDetailPage = lazy(() => import("@/features/styles/StyleDetailPage").then((m) => ({ default: m.StyleDetailPage })));
const StaffPage = lazy(() => import("@/features/staff/StaffPage").then((m) => ({ default: m.StaffPage })));
const StaffDetailPage = lazy(() => import("@/features/staff/StaffDetailPage").then((m) => ({ default: m.StaffDetailPage })));
const StaffTasksPage = lazy(() => import("@/features/staff/StaffTasksPage").then((m) => ({ default: m.StaffTasksPage })));
const SalaryPage = lazy(() => import("@/features/salaries/SalaryPage").then((m) => ({ default: m.SalaryPage })));
const SalaryPaymentPage = lazy(() => import("@/features/salaries/SalaryPaymentPage").then((m) => ({ default: m.SalaryPaymentPage })));
const TrackingPage = lazy(() => import("@/features/tracking/TrackingPage").then((m) => ({ default: m.TrackingPage })));
const WorkTrackingPage = lazy(() => import("@/features/tracking/WorkTrackingPage").then((m) => ({ default: m.WorkTrackingPage })));
const ReceiptVerificationPage = lazy(() => import("@/features/payments/ReceiptVerificationPage").then((m) => ({ default: m.ReceiptVerificationPage })));
const TemplatesPage = lazy(() => import("@/features/templates/TemplatesPage"));
const TeamInvitePage = lazy(() => import("@/features/team/TeamInvitePage").then((m) => ({ default: m.TeamInvitePage })));

function Loading() {
  return <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">Loading…</div>;
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: "/auth",
    element: <Lazy><AuthPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/reset-password",
    element: <Lazy><ResetPasswordPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/auth/team-activate",
    element: <Lazy><TeamActivationPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/track/:token",
    element: <Lazy><TrackingPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/track-work/:token",
    element: <Lazy><WorkTrackingPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/verify/:receiptId",
    element: <Lazy><ReceiptVerificationPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin/login",
    element: <Lazy><AdminLoginPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin",
    element: <Lazy><AdminDashboardPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin/plans",
    element: <Lazy><AdminPlansPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin/settings/password/edit",
    element: <Lazy><AdminDashboardPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin/tailors/new",
    element: <Lazy><AdminDashboardPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/admin/:tab",
    element: <Lazy><AdminDashboardPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/",
    element: <Lazy><LandingPage /></Lazy>,
    errorElement: <RouteErrorBoundary />
  },
  {
    path: "/app",
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Lazy><DashboardPage /></Lazy> },
      { path: "clients", element: <Lazy><ClientsPage /></Lazy> },
      { path: "clients/new", element: <Lazy><ClientCreatePage /></Lazy> },
      { path: "clients/:id/edit", element: <Lazy><ClientEditPage /></Lazy> },
      {
        path: "clients/:id",
        element: <Lazy><ClientLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><ClientOverviewTab /></Lazy> },
          { path: "orders", element: <Lazy><ClientOrdersTab /></Lazy> },
          { path: "measurements", element: <Lazy><ClientMeasurementsTab /></Lazy> },
          { path: "measurements/edit", element: <Lazy><ClientMeasurementsEditPage /></Lazy> },
          { path: "payments", element: <Lazy><ClientPaymentsTab /></Lazy> },
          { path: "styles", element: <Lazy><ClientStylesTab /></Lazy> }
        ]
      },
      { path: "measurements/new", element: <Lazy><MeasurementsPage /></Lazy> },
      { path: "measurements", element: <Lazy><MeasurementsPage /></Lazy> },
      { path: "orders/new", element: <Lazy><OrdersPage /></Lazy> },
      { path: "orders/:id/edit", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "orders/:id/items/edit", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "orders/:id/payment/new", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "orders/:id/payment/:paymentId/edit", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "orders/:id/price-adjustment/new", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "orders", element: <Lazy><OrdersPage /></Lazy> },
      { path: "orders/:id", element: <Lazy><OrderDetailPage /></Lazy> },
      { path: "payments/new", element: <Navigate to="/app/payments" replace /> },
      { path: "payments", element: <Lazy><PaymentsPage /></Lazy> },
      { path: "finance", element: <Lazy><FinancePage /></Lazy> },
      { path: "finance/expenses/new", element: <Lazy><FinancePage /></Lazy> },
      { path: "team/new", element: <Lazy><TeamInvitePage /></Lazy> },
      { path: "styles", element: <Lazy><StylesPage /></Lazy> },
      { path: "styles/new", element: <Lazy><StylesPage /></Lazy> },
      { path: "styles/:id/edit", element: <Lazy><StylesPage /></Lazy> },
      { path: "styles/:id", element: <Lazy><StyleDetailPage /></Lazy> },
      { path: "staff", element: <Lazy><StaffPage /></Lazy> },
      { path: "tasks", element: <Lazy><StaffTasksPage /></Lazy> },
      { path: "staff/new", element: <Lazy><StaffPage /></Lazy> },
      { path: "staff/:id/edit", element: <Lazy><StaffPage /></Lazy> },
      { path: "staff/:id", element: <Lazy><StaffDetailPage /></Lazy> },
      { path: "salaries", element: <Lazy><SalaryPage /></Lazy> },
      { path: "salaries/new", element: <Lazy><SalaryPage /></Lazy> },
      { path: "salaries/:id/edit", element: <Lazy><SalaryPage /></Lazy> },
      { path: "salaries/:id/pay", element: <Lazy><SalaryPaymentPage /></Lazy> },
      { path: "templates", element: <Lazy><TemplatesPage /></Lazy> },
      { path: "templates/new", element: <Lazy><TemplatesPage /></Lazy> },
      { path: "templates/:id/edit", element: <Lazy><TemplatesPage /></Lazy> },
      { path: "flows", element: <Navigate to="/app/templates" replace /> },
      { path: "flows/templates/new", element: <Navigate to="/app/templates/new" replace /> },
      { path: "flows/items/new", element: <Navigate to="/app/templates" replace /> },
      { path: "settings", element: <Lazy><SettingsPage /></Lazy> },
      { path: "settings/profile/edit", element: <Navigate to="/app/settings?section=account" replace /> },
      { path: "settings/password/edit", element: <Navigate to="/app/settings?section=security" replace /> },
      { path: "settings/support/new", element: <Navigate to="/app/settings?section=support" replace /> }
    ]
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
    errorElement: <RouteErrorBoundary />
  }
]);
